import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import MaskLines, { EASE } from '@/components/MaskLines';

function Row({ row, open, onToggle }) {
  return (
    <div className="border-t hairline last:border-b" data-testid={`whatwedo-row-${row.id}`}>
      <button
        data-testid={`whatwedo-toggle-${row.id}`}
        onClick={onToggle}
        aria-expanded={open}
        className="group flex w-full items-baseline justify-between gap-6 py-8 text-left md:py-10"
      >
        <span className="flex items-baseline gap-5 md:gap-10">
          <span className="font-mono-label text-white/40">{row.index}</span>
          <span className="font-display text-[9.5vw] leading-none transition-colors duration-300 group-hover:text-[var(--pz-green)] md:text-[5.5vw]">
            {row.label}
          </span>
        </span>
        <Plus
          size={34}
          strokeWidth={1.5}
          className={`shrink-0 self-center transition-transform duration-300 ${open ? 'rotate-45 text-[var(--pz-green)]' : 'text-white/60'}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="overflow-hidden"
            data-testid={`whatwedo-content-${row.id}`}
          >
            <div className="pb-10 pl-0 md:pl-[7.5rem]">
              {row.body && <p className="max-w-2xl text-base text-white/70 md:text-lg">{row.body}</p>}
              {row.note && <p className="mt-4 font-mono-label text-[var(--pz-green)]">{row.note}</p>}
              {row.chips && (
                <ul className="mt-6 flex flex-wrap gap-2.5">
                  {row.chips.map((chip) => (
                    <li
                      key={chip}
                      className="font-mono-label rounded-full border border-white/25 px-4 py-2 transition-colors duration-300 hover:border-[var(--pz-green)] hover:text-[var(--pz-green)]"
                    >
                      {chip}
                    </li>
                  ))}
                </ul>
              )}
              {row.items && (
                <p className="font-display mt-2 flex flex-wrap gap-x-8 gap-y-3 text-3xl md:text-5xl">
                  {row.items.map((item) => (
                    <span key={item} className="cursor-default transition-colors duration-300 hover:text-[var(--pz-green)]">
                      {item}
                    </span>
                  ))}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function WhatWeDo() {
  const { t } = useLang();
  const [open, setOpen] = useState('docenze');
  return (
    <section id="what-i-do" className="px-5 py-28 md:px-10 md:py-36" data-testid="section-whatwedo">
      <p className="font-mono-label text-[var(--pz-green)]">{t.whatwedo.kicker}</p>
      <h2 className="font-display mt-6 text-[11vw] md:text-[6.5vw] md:leading-[0.9]">
        <MaskLines lines={[t.whatwedo.title]} />
      </h2>
      <div className="mt-14 md:mt-20">
        {t.whatwedo.rows.map((row) => (
          <Row key={row.id} row={row} open={open === row.id} onToggle={() => setOpen(open === row.id ? null : row.id)} />
        ))}
      </div>
    </section>
  );
}
